/**
 * Логика раздела «Отделка стен»: элемент по id, подпись вида и итоговый вид стороны стены.
 *
 * Отделки две породы: встроенные (`finishSection` — краска и нарисованные кодом рисунки)
 * и текстуры из библиотеки (`wallTextures`, догружается отдельно — `library.ts`). Текстур
 * тысячи, поэтому в общий каталог и поиск они не идут — их показывает отдельная панель
 * «Отделка», а по id находит `finishItemById` вместе со встроенными.
 */
import type { WallFinish } from "@/shared/plan";
import { finishSection } from "./catalog";
import { wallTextures } from "./library";
import {
	WallFinishKind,
	WallPattern,
	type FinishTexture,
	type WallFinishCatalogItem,
	type WallFinishLook,
} from "./types";

export const WALL_FINISH_KIND_LABEL: Record<WallFinishKind, string> = {
	[WallFinishKind.Paint]: "Краска",
	[WallFinishKind.Wallpaper]: "Обои",
	[WallFinishKind.Plaster]: "Штукатурка",
	[WallFinishKind.Tile]: "Плитка",
	[WallFinishKind.Panel]: "Панели",
};

/** Вкладки панели «Отделка» по порядку. */
export const WALL_FINISH_KINDS: readonly WallFinishKind[] = [
	WallFinishKind.Paint,
	WallFinishKind.Wallpaper,
	WallFinishKind.Tile,
	WallFinishKind.Plaster,
	WallFinishKind.Panel,
];

const TEXTURE_KIND: Record<FinishTexture["kind"], WallFinishKind> = {
	wallpaper: WallFinishKind.Wallpaper,
	tile: WallFinishKind.Tile,
	panel: WallFinishKind.Panel,
	plaster: WallFinishKind.Plaster,
};

/** Шероховатость по виду: глазурь плитки бликует, обои и штукатурка — матовые. */
const TEXTURE_ROUGHNESS: Record<FinishTexture["kind"], number> = {
	wallpaper: 0.85,
	tile: 0.3,
	panel: 0.6,
	plaster: 0.9,
};

const textureItem = (texture: FinishTexture): WallFinishCatalogItem => {
	const kind = TEXTURE_KIND[texture.kind];
	return {
		id: texture.id,
		subGroup: WALL_FINISH_KIND_LABEL[kind],
		name: texture.name,
		description: texture.collection,
		kind,
		pattern: WallPattern.Image,
		color: texture.color,
		accent: texture.color,
		repeat: texture.width,
		roughness: TEXTURE_ROUGHNESS[texture.kind],
		collection: texture.collection,
		texture,
	};
};

/** Встроенные — коллекция «Базовые»: в панели «Отделка» они идут первыми в своей вкладке. */
export const BASE_FINISH_COLLECTION = "Базовые";

const BUILT_IN: readonly WallFinishCatalogItem[] = finishSection.items.map((item) => ({
	...item,
	collection: BASE_FINISH_COLLECTION,
}));

/** Список и поиск по id — от текущей библиотеки: пересчитываются, когда она догрузилась. */
let index: {
	source: readonly FinishTexture[];
	items: readonly WallFinishCatalogItem[];
	byId: Map<string, WallFinishCatalogItem>;
} | null = null;

const finishIndex = () => {
	const source = wallTextures();
	if (index?.source !== source) {
		const items = [...BUILT_IN, ...source.map(textureItem)];
		index = { source, items, byId: new Map(items.map((item) => [item.id, item])) };
	}
	return index;
};

/** Вся отделка стен: встроенная и текстуры библиотеки. */
export const wallFinishItems = (): readonly WallFinishCatalogItem[] => finishIndex().items;

export const finishItemById = (id: string): WallFinishCatalogItem | null =>
	finishIndex().byId.get(id) ?? null;

const channels = (hex: string): number[] | null => {
	const body = /^#([0-9a-f]{6})$/i.exec(hex)?.[1];
	return body ? [0, 2, 4].map((i) => parseInt(body.slice(i, i + 2), 16)) : null;
};

const toHex = (rgb: number[]) =>
	`#${rgb
		.map((c) =>
			Math.round(Math.max(0, Math.min(255, c)))
				.toString(16)
				.padStart(2, "0"),
		)
		.join("")}`;

/**
 * Цвет рисунка под новый основной цвет: рисунок сдвигается от фона так же, как в каталоге
 * (темнее или светлее), — иначе у перекрашенных обоев узор остался бы старого цвета.
 */
const accentFor = (item: WallFinishCatalogItem, color: string): string => {
	const base = channels(item.color);
	const accent = channels(item.accent);
	const next = channels(color);
	if (!base || !accent || !next) return item.accent;
	return toHex(next.map((c, i) => c * (accent[i] / Math.max(base[i], 1))));
};

/** Итоговый вид стороны стены; null — тип удалён из каталога. */
export const wallFinishLook = (finish: WallFinish): WallFinishLook | null => {
	const item = finishItemById(finish.finish);
	if (!item) return null;
	const color = finish.color ?? item.color;
	// У текстуры свой цвет — в картинке; выбранный цвет её подкрашивает
	if (item.texture) return { item, color, accent: color };
	return { item, color, accent: finish.color ? accentFor(item, color) : item.accent };
};
